import { useContext, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";
import { LenisContext } from "@/lenis-context";

const HERO_IMG = "/images/hero-alpage.jpg";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useLang();
  const scrollTo = useContext(LenisContext);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="accueil"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-end overflow-hidden bg-pine-deep"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt="Vallée du Haut-Bréda, massif de Belledonne"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/35" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-44 md:px-10 md:pb-28"
      >
        <div className="overflow-hidden">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6 text-xs font-semibold uppercase tracking-[0.28em] text-paper/80"
          >
            {t.hero.eyebrow}
          </motion.p>
        </div>

        <div className="overflow-hidden">
          <motion.p
            data-testid="hero-brand"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="mb-5 font-serif text-2xl italic tracking-wide text-wood-light md:text-3xl"
          >
            {t.hero.brand}
          </motion.p>
        </div>

        <h1
          data-testid="hero-title"
          className="max-w-4xl font-serif text-5xl font-light leading-[1.02] tracking-tight text-paper sm:text-6xl lg:text-8xl"
        >
          <MaskedLine delay={0.3}>{t.hero.lines[0]}</MaskedLine>
          <MaskedLine delay={0.45}>
            <span className="italic text-wood-light">{t.hero.lines[1]}</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base leading-relaxed text-paper/85 md:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            type="button"
            data-testid="hero-cta-contact"
            onClick={() => scrollTo?.("#contact")}
            className="group inline-flex items-center gap-2 rounded-full bg-paper px-7 py-3.5 text-sm font-semibold text-pine transition-all duration-300 hover:bg-wood hover:text-paper"
          >
            {t.hero.cta2}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        type="button"
        onClick={() => scrollTo?.("#histoire")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden flex-col items-center gap-3 text-paper/70 transition-colors hover:text-paper md:flex"
        aria-label={t.hero.scroll}
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.3em] [writing-mode:vertical-rl]">
          {t.hero.scroll}
        </span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
}
